import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { Button as ButtonMUI } from '@material-ui/core';
import dashboardColors from '../helpers/dashboardColors';

const colors = theme => ({
  primary: theme.colors.secondary,
  primaryHover: theme.colors.institucionalComplementDark,
  secondary: theme.colors.white,
  secondaryHover: theme.colors.gray100,
  institutional: theme.colors.institucional,
  institutionalHover: theme.colors.institucionalDark,
  caixa: theme.colors.secondary,
  caixaHover: theme.colors.white,
  27: dashboardColors(theme, 27).color3,
  201: dashboardColors(theme, 201).color3,
  202: dashboardColors(theme, 202).color3,
  31: dashboardColors(theme, 31).color3,
  28: dashboardColors(theme, 28).color3,
  30: dashboardColors(theme, 30).color3,
  204: dashboardColors(theme, 204).color3,
  243: dashboardColors(theme, 243).color3,
  203: dashboardColors(theme, 203).color3,
  207: dashboardColors(theme, 207).color3,
  208: dashboardColors(theme, 208).color3,
  209: dashboardColors(theme, 209).color3,
  681: dashboardColors(theme, 681).color3,
  270: dashboardColors(theme, 270).color3,
  290: dashboardColors(theme, 290).color3,
});

const bgColors = theme => ({
  primary: theme.colors.secondary,
  primaryHover: theme.colors.white,
  secondary: theme.colors.white,
  secondaryHover: theme.colors.white,
  institutional: theme.colors.institucional,
  institutionalHover: theme.colors.institucionalDark,
  institutionalOutlined: theme.colors.institucionalDark,
  caixa: theme.colors.secondary,
  caixaHover: theme.colors.secondary,
  caixaHoverOutlined: theme.colors.secondary,
  27: dashboardColors(theme, 27).color1,
  201: dashboardColors(theme, 201).color1,
  202: dashboardColors(theme, 202).color1,
  31: dashboardColors(theme, 31).color1,
  28: dashboardColors(theme, 28).color1,
  30: dashboardColors(theme, 30).color1,
  204: dashboardColors(theme, 204).color1,
  243: dashboardColors(theme, 243).color1,
  203: dashboardColors(theme, 203).color1,
});

const bgColorsContrast = theme => ({
  primary: theme.colors.primary,
  primaryHover: theme.colors.institucionalLight,
});

const fontColors = theme => ({
  primary: theme.colors.institucionalDark,
  primaryHover: theme.colors.institucional,
  secondary: theme.colors.institucional,
  secondaryHover: theme.colors.institucionalDark,
  institutional: theme.colors.white,
  institutionalHover: theme.colors.white,
  caixa: theme.colors.white,
  caixaHover: theme.colors.white,
  27: 'white',
  201: 'white',
  202: 'white',
  31: 'white',
  28: 'white',
  30: 'white',
  204: 'white',
  243: 'white',
  203: 'white',
});

const fontColorsContrast = theme => ({
  primary: theme.colors.white,
  primaryHover: theme.colors.white,
});

const contained = css`
  background: ${({ theme, componentColor, contrast }) =>
    contrast
      ? bgColorsContrast(theme)[componentColor]
      : bgColors(theme)[componentColor]};
  color: ${({ theme, componentColor, contrast }) =>
    contrast
      ? fontColorsContrast(theme)[componentColor]
      : fontColors(theme)[componentColor]};

  &:hover {
    background: ${({ theme, componentColor, contrast }) =>
      contrast
        ? bgColorsContrast(theme)[`${componentColor}Hover`]
        : bgColors(theme)[`${componentColor}Hover`]};
    color: ${({ theme, componentColor, contrast }) =>
      contrast
        ? fontColorsContrast(theme)[`${componentColor}Hover`]
        : fontColors(theme)[`${componentColor}Hover`]};
  }

  &:disabled {
    background: ${({ theme }) => theme.colors.disabled};
    color: ${({ theme }) => theme.colors.disabledColor};
    cursor: not-allowed;
    pointer-events: all !important;

    &:hover {
      background: ${({ theme }) => theme.colors.disabled};
    }
  }
`;

const outlined = css`
  border: 2px solid
    ${({ theme, componentColor }) => colors(theme)[componentColor]} !important;
  color: ${({ theme, componentColor }) =>
    colors(theme)[componentColor]} !important;

  &:hover {
    background: ${({ theme, componentColor }) =>
      bgColors(theme)[`${componentColor}HoverOutlined`]};
    color: ${({ theme, componentColor }) =>
      fontColors(theme)[`${componentColor}Hover`]};
  }

  &:disabled {
    border-width: 2px;
    cursor: not-allowed;
    pointer-events: all !important;
  }
`;

const transparent = css`
  color: ${({ theme }) => theme.colors.white};
  font-weight: normal;

  &:disabled {
    color: ${({ theme }) => theme.colors.white};
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: all !important;
  }
  &:hover {
    background: transparent;
  }
`;

const ComponentStyled = styled(
  ({ componentVariant, componentColor, contrast, ...rest }) => (
    <ButtonMUI {...rest} />
  ),
)`
  &.MuiButtonBase-root {
    text-transform: none;
    font-weight: 600;
    letter-spacing: 0.4px;
    padding: ${({ size }) => !size && '14px 28px'};
    border-radius: 5px;
    outline: none;
    max-width: ${({ fullWidth }) => (fullWidth ? 'unset' : '333px')};
    ${({ componentVariant }) => {
      switch (componentVariant) {
        case 'contained':
          return contained;
        case 'outlined':
          return outlined;
        case 'transparent':
          return transparent;
        default:
          return contained;
      }
    }}
  }

  .MuiButton-iconSizeMedium > *:first-child {
    font-size: 16px;
  }
`;

const Button = ({
  variant = 'contained',
  color = 'primary',
  label,
  contrast,
  disabled,
  ariaLabel,
  ...props
}) => {
  return (
    // https://material-ui.com/components/buttons/
    <ComponentStyled
      aria-label={ariaLabel}
      componentColor={color}
      componentVariant={disabled ? 'contained' : variant}
      variant={variant === 'outlined' && !disabled ? variant : undefined}
      contrast={contrast}
      disabled={disabled}
      {...props}
    >
      {label}
    </ComponentStyled>
  );
};

Button.propTypes = {
  label: PropTypes.string,
  variant: PropTypes.string,
  color: PropTypes.any,
  contrast: PropTypes.bool,
  disabled: PropTypes.bool,
  fullWidth: PropTypes.bool,
  ariaLabel: PropTypes.string,
};

export default Button;
